import { createLeadsService } from '../services/leadsFactory';
import type { FormData } from '../types/form';
import { ApiError, type CreateContactResponse, type AvailabilityResponse, type KbaQuestion, type StartAutoEchelonResponse } from '../types/api';
import type { ILeadsService } from '../services/leads';
import { storageController } from './StorageController';

export type AutoEchelonResult = {
  success: boolean;
  redirectUrl?: string;
  error?: string;
};

export type EchelonProgressStage = 'starting' | 'processing' | 'reviewing' | 'finalizing';

type AutoEchelonOptions = {
  initialDelayMs?: number;
  intervalMs?: number;
  maxAttempts?: number;
  startRetryIntervalMs?: number;
  finalizingAfterAttempts?: number;
  onProgress?: (stage: EchelonProgressStage) => void;
};

export type ContactResult = {
  success: boolean;
  status?: 'accepted' | 'accepted_tier2' | 'rejected' | 'ERROR';
  error?: string;
  code?: number;
};

export type AvailabilityResult = {
  success: boolean;
  capacity?: number;
  availability: 'none' | 'low' | 'high';
  error?: string;
};

export type ScheduleResult = {
  success: boolean;
  error?: string;
};

export type CallResult = {
  success: boolean;
  from?: string;
  error?: string;
};

export type KbaConnectResult = {
  success: boolean;
  questions?: KbaQuestion[];
  connectionStatus?: string;
  error?: string;
  code?: number;
};

export type KbaVerifyResult = {
  success: boolean;
  connectionStatus?: string;
  questions?: KbaQuestion[];
  error?: string;
  code?: number;
};

export type TimeslotData = {
  timeSlot: string;
  isAvailable: boolean;
  capacity: number;
  booked: number;
  slotsAvailable: number;
};

export type DateAvailabilityResult = {
  success: boolean;
  date: string;
  totalCalls?: number;
  timeslots: TimeslotData[];
  error?: string;
  cachedAt?: number;
};

type AvailabilityCache = {
  [date: string]: {
    data: DateAvailabilityResult;
    cachedAt: number;
  };
};

const STORAGE_KEYS = {
  SPINWHEEL_ID: 'spinwheel_id',
  HUBSPOT_RECORD_ID: 'hubspot_record_id',
  VERIFICATION_ATTEMPTS: 'verification_attempts',
  CONTACT_FIRST_NAME: 'contact_first_name',
  DEBT_AMOUNT: 'debt_amount',
  AVAILABILITY_CACHE: 'availability_cache',
} as const;

const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour in milliseconds

export class FormController {
  private readonly leadsService: ILeadsService;
  private spinwheelId: string = '';
  private hubspotRecordId: string = '';
  private verificationAttempts: number = 0;
  private contactFirstName: string = '';
  private debtAmount: number = 0;
  private availabilityCache: AvailabilityCache = {};

  constructor(leadsService?: ILeadsService) {
    this.leadsService = leadsService ?? createLeadsService();
    this.loadFromStorage();
  }

  private loadFromStorage() {
    // Load cached values from storage
    this.spinwheelId = localStorage.getItem(STORAGE_KEYS.SPINWHEEL_ID) || '';
    this.hubspotRecordId = localStorage.getItem(STORAGE_KEYS.HUBSPOT_RECORD_ID) || '';
    this.verificationAttempts = parseInt(localStorage.getItem(STORAGE_KEYS.VERIFICATION_ATTEMPTS) || '0', 10);
    this.contactFirstName = localStorage.getItem(STORAGE_KEYS.CONTACT_FIRST_NAME) || '';
    this.debtAmount = parseInt(localStorage.getItem(STORAGE_KEYS.DEBT_AMOUNT) || '0', 10);
    
    // Load availability cache
    const cachedAvailability = sessionStorage.getItem(STORAGE_KEYS.AVAILABILITY_CACHE);
    if (cachedAvailability) {
      try {
        this.availabilityCache = JSON.parse(cachedAvailability);
        // Clean up expired cache entries
        this.cleanupExpiredCache();
      } catch (error) {
        console.warn('Failed to parse availability cache:', error);
        this.availabilityCache = {};
      }
    }
  }

  private saveToStorage() {
    // Save current values to storage
    localStorage.setItem(STORAGE_KEYS.SPINWHEEL_ID, this.spinwheelId);
    localStorage.setItem(STORAGE_KEYS.HUBSPOT_RECORD_ID, this.hubspotRecordId);
    localStorage.setItem(STORAGE_KEYS.VERIFICATION_ATTEMPTS, this.verificationAttempts.toString());
    localStorage.setItem(STORAGE_KEYS.CONTACT_FIRST_NAME, this.contactFirstName);
    localStorage.setItem(STORAGE_KEYS.DEBT_AMOUNT, this.debtAmount.toString());
  }

  private clearStorage() {
    // Clear all stored values
    localStorage.removeItem(STORAGE_KEYS.SPINWHEEL_ID);
    localStorage.removeItem(STORAGE_KEYS.HUBSPOT_RECORD_ID);
    localStorage.removeItem(STORAGE_KEYS.VERIFICATION_ATTEMPTS);
    localStorage.removeItem(STORAGE_KEYS.CONTACT_FIRST_NAME);
    localStorage.removeItem(STORAGE_KEYS.DEBT_AMOUNT);
    sessionStorage.removeItem(STORAGE_KEYS.AVAILABILITY_CACHE);
  }

  private saveAvailabilityCache() {
    try {
      sessionStorage.setItem(STORAGE_KEYS.AVAILABILITY_CACHE, JSON.stringify(this.availabilityCache));
    } catch (error) {
      console.warn('Failed to save availability cache:', error);
    }
  }

  private cleanupExpiredCache() {
    const now = Date.now();
    const expiredKeys: string[] = [];
    
    for (const [date, cacheEntry] of Object.entries(this.availabilityCache)) {
      if (now - cacheEntry.cachedAt > CACHE_TTL_MS) {
        expiredKeys.push(date);
      }
    }
    
    expiredKeys.forEach(key => {
      delete this.availabilityCache[key];
    });
    
    if (expiredKeys.length > 0) {
      this.saveAvailabilityCache();
    }
  }

  private isCacheValid(date: string): boolean {
    const cacheEntry = this.availabilityCache[date];
    if (!cacheEntry) {
      return false;
    }
    
    const now = Date.now();
    return (now - cacheEntry.cachedAt) < CACHE_TTL_MS;
  }

  public getContactFirstName(): string {
    return this.contactFirstName;
  }

  public getDebtAmount(): number {
    return this.debtAmount;
  }

  private handleApiError(err: unknown): { success: false; error: string; code?: number } {
    if (err instanceof ApiError) {
      return { 
        success: false, 
        error: err.status.message,
        code: err.status.code
      };
    }

    if (err && typeof err === 'object' && 'status' in err) {
      const status = (err as any).status;
      if (status && typeof status === 'object' && 'code' in status && 'message' in status) {
        return {
          success: false,
          error: status.message,
          code: status.code
        };
      }
    }
    
    return { 
      success: false, 
      error: err instanceof Error ? err.message : 'An error occurred' 
    };
  }

  private isSuccessStatus(code: number): boolean {
    return code >= 200 && code < 300;
  }

  private getAvailabilityLevel(capacity: number): 'none' | 'low' | 'high' {
    if (capacity === 0) return 'none';
    if (capacity <= 2) return 'low';
    return 'high';
  }

  async connectBySms(
    phoneNumber: string, 
    email: string, 
    dateOfBirth: string,
    loanAmount: number | undefined,
    loanPurpose: string | undefined,
    employmentStatus: string | undefined,
    employmentFrequency: string | undefined,
    educationLevel: string | undefined,
    annualIncome: number | undefined,
    propertyStatus: string | undefined,
    ipAddress: string,
    marketingParams: Record<string, string>
  ): Promise<{ success: boolean; error?: string; code?: number }> {
    try {
      const response = await this.leadsService.connectBySms(
        phoneNumber,
        email,
        dateOfBirth,
        loanAmount,
        loanPurpose,
        employmentStatus,
        employmentFrequency,
        educationLevel,
        annualIncome,
        propertyStatus,
        ipAddress,
        marketingParams
      );

      if (this.isSuccessStatus(response.status.code) && response.spinwheelId) {
        this.spinwheelId = response.spinwheelId;
        if (response.hubspotRecordId) {
          this.hubspotRecordId = response.hubspotRecordId;
        }
        this.saveToStorage();
        return { success: true };
      }
      
      return { 
        success: false, 
        error: 'Failed to create connection' 
      };
    } catch (err) {
      return this.handleApiError(err);
    }
  }

  async verifyCode(code: string, formData: FormData): Promise<{ success: boolean; error?: string; code?: number }> {
    if (!this.spinwheelId || !this.hubspotRecordId) {
      return {
        success: false,
        error: 'Missing required verification data'
      };
    }

    try {
      const response = await this.leadsService.verifyCode(
        code,
        formData.phone,
        this.spinwheelId,
        formData.email,
        this.hubspotRecordId
      );

      return {
        success: this.isSuccessStatus(response.status.code),
        error: this.isSuccessStatus(response.status.code) ? undefined : 'Failed to verify code'
      };
    } catch (err) {
      return this.handleApiError(err);
    }
  }

  async createContact(formData: FormData): Promise<ContactResult> {
    if (!this.spinwheelId || !this.hubspotRecordId) {
      return {
        success: false,
        status: 'ERROR',
        error: 'Missing required contact data'
      };
    }

    try {
      const response = await this.leadsService.createContact(
        this.spinwheelId,
        formData.email,
        this.hubspotRecordId
      );

      // Track verification attempts
      this.verificationAttempts++;

      // Store the first name and debt amount from the response
      if (response.firstName) {
        this.contactFirstName = response.firstName;
        storageController.setContactFirstName(response.firstName);
      }
      if (response.debtAmount) {
        this.debtAmount = response.debtAmount;
        storageController.setDebtAmount(response.debtAmount);
      }
      this.saveToStorage();

      // Handle qualified status
      if (response.state === 'accepted') {
        // Check for tier 2 qualification
        if (response.debtType === 'debt2') {
          return {
            success: true,
            status: 'accepted_tier2'
          };
        }
        return {
          success: true,
          status: 'accepted'
        };
      }

      // Handle unqualified status
      if (response.state === 'rejected') {
        return {
          success: true,
          status: 'rejected'
        };
      }

      // Handle error case
      return {
        success: false,
        status: 'ERROR',
        error: response.status.message || 'Failed to create contact'
      };
    } catch (err) {
      if (err instanceof ApiError && err.status.code === 422) {
        return {
          success: true,
          status: 'rejected'
        };
      }
      
      // Track failed attempt
      this.verificationAttempts++;
      this.saveToStorage();

      const error = this.handleApiError(err);
      return {
        success: false,
        status: 'ERROR',
        error: error.error,
        code: error.code
      };
    }
  }

  getVerificationAttempts(): number {
    return this.verificationAttempts;
  }

  async fallbackToExperian(data: Partial<FormData>): Promise<{ success: boolean; qualified: boolean; code?: number }> {
    try {
      const experianResponse = await this.leadsService.createContactExperian(
        data.email!,
        data.firstName!,
        data.lastName!,
        data.address1!,
        data.city!,
        data.state!,
        data.zipCode!,
        data.birthDate || '',
        data.phone || ''
      );

      if (experianResponse.firstName) {
        this.contactFirstName = experianResponse.firstName;
        storageController.setContactFirstName(experianResponse.firstName);
      }
      if (experianResponse.debtAmount) {
        this.debtAmount = experianResponse.debtAmount;
        storageController.setDebtAmount(experianResponse.debtAmount);
      }
      this.saveToStorage();

      if (experianResponse.state === 'accepted') {
        return { success: true, qualified: true };
      }

      if (experianResponse.state === 'rejected') {
        return { success: true, qualified: false };
      }

      return { success: false, qualified: false };
    } catch (err) {
      const error = this.handleApiError(err);
      return { success: false, qualified: false, code: error.code };
    }
  }

  async prospectVerify(data: Partial<FormData>): Promise<{ success: boolean; qualified: boolean }> {
    try {
      const response = await this.leadsService.prospectVerify(
        data.email!,
        data.firstName!,
        data.lastName!,
        data.address1!,
        data.city!,
        data.state!,
        data.zipCode!,
        data.offerCode!,
        data.phone || '',
        data.birthDate || ''
      );

      if (response.firstName) {
        this.contactFirstName = response.firstName;
        storageController.setContactFirstName(response.firstName);
      }
      if (response.debtAmount) {
        this.debtAmount = response.debtAmount;
        storageController.setDebtAmount(response.debtAmount);
      }
      this.saveToStorage();

      return {
        success: true,
        qualified: response.prospectStatus === 'QUALIFIED'
      };
    } catch (err) {
      return { success: false, qualified: false };
    }
  }

async createManualLead(data: Partial<FormData>): Promise<{ success: boolean; qualified: boolean; code?: number }> {
  try {
    const response = await this.leadsService.createManualLead(
      data.email!,
      data.firstName!,
      data.lastName!,
      data.address1!,
      data.city!,
      data.state!,
      data.zipCode!,
      data.offerCode || '',
      data.phone || '',
      data.birthDate || ''
    );

    if (response.firstName) {
      this.contactFirstName = response.firstName;
      storageController.setContactFirstName(response.firstName);
    }
    if (response.debtAmount) {
      this.debtAmount = response.debtAmount;
      storageController.setDebtAmount(response.debtAmount);
    }
    this.saveToStorage();

    if (response.state === 'accepted') {
      return { success: true, qualified: true };
    }

    if (response.state === 'rejected') {
      return { success: true, qualified: false };
    }

    return { success: false, qualified: false };
    } catch (err) {
      const error = this.handleApiError(err);
      return { success: false, qualified: false, code: error.code };
    }
  }

  async submitApplication(formData: FormData, ipAddress: string): Promise<{ success: boolean; error?: string; offerId?: string, offerCount: number, storeUuid: string }> {
    try {
      const response = await this.leadsService.submitApplication(
        formData.email,
        formData.ssnLast4 || '',
        formData.loanAmount,
        formData.loanPurpose,
        formData.employmentStatus,
        formData.employmentFrequency,
        formData.educationLevel,
        formData.annualIncome,
        formData.propertyStatus,
        ipAddress,
        this.hubspotRecordId || undefined
      );

      return {
        success: this.isSuccessStatus(response.status.code),
        offerId: response.offerId,
        offerCount: response.offerCount,
        storeUuid: response.storeUuid,
        error: this.isSuccessStatus(response.status.code) ? undefined : 'Failed to submit application'
      };
    } catch (err) {
      const error = this.handleApiError(err);
      return {
        success: false,
        error: error.error,
        offerCount: 0,
        storeUuid: ''
      };
    }
  }

  async checkContactCenterAvailability(): Promise<AvailabilityResult> {
    try {
      const response = await this.leadsService.getContactCenterAvailability();
      const capacity = response.capacity || 0;
      
      return {
        success: this.isSuccessStatus(response.status.code),
        capacity,
        availability: this.getAvailabilityLevel(capacity)
      };
    } catch (err) {
      const error = this.handleApiError(err);
      return {
        ...error,
        availability: 'none'
      };
    }
  }

  async scheduleAppointment(meetingTime: Date): Promise<ScheduleResult> {
    if (!this.hubspotRecordId) {
      return {
        success: false,
        error: 'Missing required HubSpot record ID'
      };
    }

    try {
      const response = await this.leadsService.scheduleAppointment(
        this.hubspotRecordId,
        meetingTime.toISOString()
      );

      return {
        success: this.isSuccessStatus(response.status.code),
        error: this.isSuccessStatus(response.status.code) ? undefined : 'Failed to schedule appointment'
      };
    } catch (err) {
      return this.handleApiError(err);
    }
  }

  async connectCall(): Promise<CallResult> {
    if (!this.hubspotRecordId) {
      return {
        success: false,
        error: 'Missing required HubSpot record ID'
      };
    }

    try {
      const response = await this.leadsService.connectCall(this.hubspotRecordId);
      
      return {
        success: this.isSuccessStatus(response.status.code),
        from: response.from,
        error: this.isSuccessStatus(response.status.code) ? undefined : 'Failed to connect call'
      };
    } catch (err) {
      return this.handleApiError(err);
    }
  }

  async connectKba(formData: FormData, ipAddress: string, kbaMarketingParams: Record<string, string>): Promise<KbaConnectResult> {
    try {
      const response = await this.leadsService.connectKba(
        formData.email,
        formData.phone,
        formData.firstName || '',
        formData.lastName || '',
        formData.birthDate,
        formData.fullSsn || '',
        {
          street: formData.address1 || '',
          city: formData.city || '',
          state: formData.state || '',
          zipCode: formData.zipCode || ''
        },
        ipAddress,
        kbaMarketingParams
      );

      if (response.spinwheelId) {
        this.spinwheelId = response.spinwheelId;
      }
      if (response.hubspotRecordId) {
        this.hubspotRecordId = response.hubspotRecordId;
      }
      this.saveToStorage();

      return {
        success: this.isSuccessStatus(response.status.code),
        questions: response.kbaQuestions,
        connectionStatus: response.connectionStatus,
        error: this.isSuccessStatus(response.status.code) ? undefined : response.status.message
      };
    } catch (err) {
      const error = this.handleApiError(err);
      return { success: false, error: error.error, code: error.code };
    }
  }

  async verifyKba(answers: Record<string, string>, email: string): Promise<KbaVerifyResult> {
    if (!this.spinwheelId || !this.hubspotRecordId) {
      return {
        success: false,
        error: 'Missing required KBA session data'
      };
    }

    try {
      const response = await this.leadsService.verifyKba(
        this.spinwheelId,
        this.hubspotRecordId,
        answers,
        email
      );

      return {
        success: this.isSuccessStatus(response.status.code),
        connectionStatus: response.connectionStatus,
        questions: response.kbaQuestions,
        error: this.isSuccessStatus(response.status.code) ? undefined : response.status.message
      };
    } catch (err) {
      const error = this.handleApiError(err);
      return { success: false, error: error.error, code: error.code };
    }
  }

  // Track which success page variant was shown to customer
  async successPage(variantId: string): Promise<{ success: boolean; error?: string }> {
    if (!this.hubspotRecordId) {
      return {
        success: true
      };
    }
    try {
      const response = await this.leadsService.successPage(this.hubspotRecordId, variantId);
      return { success: this.isSuccessStatus(response.status.code) };
    } catch (err) {
      return { success: true };
    }
  }

  /**
   * Get availability for a specific date with caching
   * @param date - Date string in YYYY-MM-DD format or Date object
   * @returns Promise<DateAvailabilityResult> - Availability data with caching
   */
  async getDateAvailability(date: string | Date): Promise<DateAvailabilityResult> {
    // Normalize date to string format
    const dateString = typeof date === 'string' ? date : date.toISOString().split('T')[0];
    
    // Check cache first
    if (this.isCacheValid(dateString)) {
      return this.availabilityCache[dateString].data;
    }

    try {
      const response = await this.leadsService.getAvailability(dateString);
      
      let result: DateAvailabilityResult;
      
      if (!this.isSuccessStatus(response.status.code)) {
        result = {
          success: false,
          date: dateString,
          timeslots: [],
          error: response.status.message,
          cachedAt: Date.now()
        };
      } else {
        // Transform the API response into our format
        const timeslots: TimeslotData[] = Object.entries(response.data).map(([timeSlot, data]) => ({
          timeSlot,
          isAvailable: data.isAvailable,
          capacity: data.capacity,
          booked: data.booked,
          slotsAvailable: data.slotsAvailable
        }));

        result = {
          success: true,
          date: dateString,
          totalCalls: response.totalCalls,
          timeslots,
          cachedAt: Date.now()
        };
      }

      // Cache the result
      this.availabilityCache[dateString] = {
        data: result,
        cachedAt: Date.now()
      };
      
      this.saveAvailabilityCache();
      
      return result;
    } catch (err) {
      const error = this.handleApiError(err);
      
      const result: DateAvailabilityResult = {
        success: false,
        date: dateString,
        timeslots: [],
        error: error.error,
        cachedAt: Date.now()
      };
      
      // Cache error results for a shorter time to avoid repeated failures
      this.availabilityCache[dateString] = {
        data: result,
        cachedAt: Date.now()
      };
      
      this.saveAvailabilityCache();
      
      return result;
    }
  }

  /**
   * Check if a specific timeslot is available on a given date
   * @param date - Date string in YYYY-MM-DD format or Date object
   * @param timeSlot - Time slot in HH:MM format (e.g., "09:00")
   * @returns Promise<boolean> - True if available
   */
  async isTimeslotAvailable(date: string | Date, timeSlot: string): Promise<boolean> {
    const availability = await this.getDateAvailability(date);
    
    if (!availability.success) {
      return false;
    }
    
    const slot = availability.timeslots.find(slot => slot.timeSlot === timeSlot);
    return slot ? slot.isAvailable : false;
  }

  /**
   * Get all available timeslots for a specific date
   * @param date - Date string in YYYY-MM-DD format or Date object
   * @returns Promise<string[]> - Array of available timeslots in HH:MM format
   */
  async getAvailableTimeslots(date: string | Date): Promise<string[]> {
    const availability = await this.getDateAvailability(date);
    
    if (!availability.success) {
      return [];
    }
    
    return availability.timeslots
      .filter(slot => slot.isAvailable)
      .map(slot => slot.timeSlot);
  }

  /**
   * Clear availability cache (useful for testing or manual refresh)
   */
  clearAvailabilityCache(): void {
    this.availabilityCache = {};
    sessionStorage.removeItem(STORAGE_KEYS.AVAILABILITY_CACHE);
  }

  private async startAutoEchelonWithRetry(options: {
    maxRetries: number;
    retryIntervalMs: number;
  }): Promise<StartAutoEchelonResponse> {
    const { maxRetries, retryIntervalMs } = options;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await this.leadsService.startAutoEchelon(this.hubspotRecordId);
      } catch (err) {
        const is500 = err instanceof ApiError && err.status.code === 500;
        const hasRetriesLeft = attempt < maxRetries;

        if (!is500 && err instanceof ApiError && hasRetriesLeft) {
          await new Promise<void>(resolve => setTimeout(resolve, retryIntervalMs));
          continue;
        }

        throw err;
      }
    }

    throw new Error('Failed to start auto-echelon after retries');
  }

  private async pollUntilOpportunityResolves(options: {
    dealId: string;
    opportunityId: string;
    maxAttempts: number;
    intervalMs: number;
    finalizingAfterAttempts?: number;
    onProgress?: (stage: EchelonProgressStage) => void;
  }): Promise<{ outcome: 'SUCCESS'; redirectUrl: string } | { outcome: 'TERMINAL_FAILURE' }> {
    const { dealId, opportunityId, maxAttempts, intervalMs, finalizingAfterAttempts, onProgress } = options;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      if (finalizingAfterAttempts !== undefined && attempt === finalizingAfterAttempts) {
        onProgress?.('finalizing');
      }

      const response = await this.leadsService.getOpportunityStatus(dealId, opportunityId);

      if (response.status.code !== 200) {
        return { outcome: 'TERMINAL_FAILURE' };
      }

      if (response.completed) {
        if (response.succeeded && response.aeClientLink) {
          return { outcome: 'SUCCESS', redirectUrl: response.aeClientLink };
        }
        return { outcome: 'TERMINAL_FAILURE' };
      }

      const isLastAttempt = attempt === maxAttempts - 1;
      if (!isLastAttempt) {
        await new Promise<void>(resolve => setTimeout(resolve, intervalMs));
      }
    }

    return { outcome: 'TERMINAL_FAILURE' };
  }

  async initiateAutoEchelonFlow(options: AutoEchelonOptions = {}): Promise<AutoEchelonResult> {
    const {
      initialDelayMs = 5000,
      intervalMs = 5000,
      maxAttempts = 24,
      startRetryIntervalMs = 5000,
      finalizingAfterAttempts = 6,
      onProgress,
    } = options;

    if (!this.hubspotRecordId) {
      return { success: false, error: 'Missing HubSpot record ID' };
    }

    try {
      onProgress?.('starting');

      const startResponse = await this.startAutoEchelonWithRetry({
        maxRetries: 10,
        retryIntervalMs: startRetryIntervalMs,
      });

      if (!startResponse.opportunityId) {
        return { success: false, error: 'No opportunity ID returned from auto-echelon' };
      }

      if (!startResponse.dealId) {
        return { success: false, error: 'No deal ID returned from auto-echelon' };
      }

      onProgress?.('processing');
      await new Promise<void>(resolve => setTimeout(resolve, initialDelayMs));

      onProgress?.('reviewing');

      const pollResult = await this.pollUntilOpportunityResolves({
        dealId: startResponse.dealId,
        opportunityId: startResponse.opportunityId,
        maxAttempts,
        intervalMs,
        finalizingAfterAttempts,
        onProgress,
      });

      if (pollResult.outcome === 'SUCCESS') {
        return { success: true, redirectUrl: pollResult.redirectUrl };
      }

      return { success: false };
    } catch (err) {
      const error = this.handleApiError(err);
      return { success: false, error: error.error };
    }
  }

  // Reset all session data
  public reset(): void {
    this.spinwheelId = '';
    this.hubspotRecordId = '';
    this.verificationAttempts = 0;
    this.contactFirstName = '';
    this.debtAmount = 0;
    this.clearStorage();
  }
}

// Export singleton instance for global access
export const formController = new FormController();